import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Building2, Mail, Phone, MapPin, Pencil, Trash2, Users, MonitorSmartphone, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useTenants, useTenantInvitations, useTenantDeletions } from "@/hooks/useTenants";
import { TENANT_STATUS_CONFIG, INDUSTRY_OPTIONS, COMPANY_SIZE_OPTIONS } from "@/types/tenant";
import { format } from "date-fns";
import { useState } from "react";
import { RequestDeletionDialog } from "@/components/tenants/RequestDeletionDialog";

export default function TenantDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getTenantById } = useTenants();
  const { data: tenant, isLoading } = getTenantById(id || "");
  const { invitations } = useTenantInvitations(id);
  const { deletions } = useTenantDeletions();
  const [deletionDialogOpen, setDeletionDialogOpen] = useState(false);

  const pendingDeletion = deletions.find(d => d.customer_id === id && d.status === 'pending');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold">Tenant not found</h2>
        <p className="text-muted-foreground mt-2">The tenant you're looking for doesn't exist.</p>
        <Button asChild className="mt-4">
          <Link to="/admin/tenants">Back to Tenants</Link>
        </Button>
      </div>
    );
  }

  const statusConfig = TENANT_STATUS_CONFIG[tenant.tenant_status] || TENANT_STATUS_CONFIG.active;
  const industryLabel = INDUSTRY_OPTIONS.find(i => i.value === tenant.industry)?.label || tenant.industry;
  const companySizeLabel = COMPANY_SIZE_OPTIONS.find(s => s.value === tenant.company_size)?.label || tenant.company_size;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin/tenants")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-3">
            {tenant.logo_url ? (
              <img 
                src={tenant.logo_url} 
                alt={tenant.name} 
                className="h-12 w-12 rounded-lg object-cover"
              />
            ) : (
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Building2 className="h-6 w-6 text-primary" />
              </div>
            )}
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-foreground">{tenant.name}</h1>
                <Badge variant="secondary" className={`${statusConfig.bgColor} ${statusConfig.color} border-0`}>
                  {statusConfig.label}
                </Badge>
              </div>
              <p className="text-muted-foreground">{tenant.email}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild>
            <Link to={`/admin/tenants/${id}/edit`}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </Link>
          </Button>
          {tenant.tenant_status !== 'deleted' && !pendingDeletion && (
            <Button variant="destructive" onClick={() => setDeletionDialogOpen(true)}>
              <Trash2 className="h-4 w-4 mr-2" />
              Request Deletion
            </Button>
          )}
        </div>
      </div>

      {/* Pending Deletion Banner */}
      {pendingDeletion && (
        <Card className="border-orange-300 bg-orange-50 dark:bg-orange-950/20">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-orange-700 dark:text-orange-400">
              <Trash2 className="h-4 w-4" />
              <span className="font-medium">Deletion Request Pending</span>
            </div>
            <p className="text-sm text-orange-600 dark:text-orange-500 mt-1">
              Requested on {format(new Date(pendingDeletion.created_at), "MMM d, yyyy")}
              {pendingDeletion.reason && ` - Reason: ${pendingDeletion.reason}`}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Devices Enrolled</CardTitle>
            <MonitorSmartphone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{tenant.device_count}</div>
            {tenant.expected_device_count && tenant.expected_device_count > 0 && (
              <p className="text-xs text-muted-foreground">
                Expected: {tenant.expected_device_count}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Admin Invitations</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{invitations.length}</div>
            <p className="text-xs text-muted-foreground">
              {invitations.filter(i => i.status === 'pending').length} pending
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Created</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {tenant.created_at ? format(new Date(tenant.created_at), "MMM d") : "-"}
            </div>
            <p className="text-xs text-muted-foreground">
              {tenant.created_at ? format(new Date(tenant.created_at), "yyyy") : ""}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Business Information */}
        <Card>
          <CardHeader>
            <CardTitle>Business Information</CardTitle>
            <CardDescription>Company details and profile</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground">Industry</label>
              <p className="font-medium">{industryLabel || "-"}</p>
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Company Size</label>
              <p className="font-medium">{companySizeLabel || "-"}</p>
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Subscription Tier</label>
              <p className="font-medium capitalize">{tenant.subscription_tier || "-"}</p>
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card>
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
            <CardDescription>Primary contact details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span>{tenant.email}</span>
            </div>
            {tenant.phone && (
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span>{tenant.phone}</span>
              </div>
            )}
            {(tenant.address || tenant.city || tenant.state) && (
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div>
                  {tenant.address && <p>{tenant.address}</p>}
                  <p>
                    {[tenant.city, tenant.state, tenant.zip].filter(Boolean).join(", ")}
                  </p>
                  {tenant.country && <p>{tenant.country}</p>}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Billing Contact */}
        <Card>
          <CardHeader>
            <CardTitle>Billing Contact</CardTitle>
            <CardDescription>Billing and invoice details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground">Contact Name</label>
              <p className="font-medium">{tenant.billing_contact_name || "-"}</p>
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Contact Email</label>
              <p className="font-medium">{tenant.billing_contact_email || "-"}</p>
            </div>
          </CardContent>
        </Card>

        {/* Admin Invitations */}
        <Card>
          <CardHeader>
            <CardTitle>Admin Invitations</CardTitle>
            <CardDescription>Invited administrators for this tenant</CardDescription>
          </CardHeader>
          <CardContent>
            {invitations.length === 0 ? (
              <p className="text-muted-foreground text-sm">No admin invitations sent</p>
            ) : (
              <div className="space-y-3">
                {invitations.map((invitation) => (
                  <div key={invitation.id} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div>
                      <p className="font-medium">{invitation.name || invitation.email}</p>
                      <p className="text-sm text-muted-foreground">{invitation.email}</p>
                    </div>
                    <Badge variant={
                      invitation.status === 'accepted' ? 'default' :
                      invitation.status === 'pending' ? 'secondary' :
                      'outline'
                    }>
                      {invitation.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Deletion Dialog */}
      <RequestDeletionDialog
        open={deletionDialogOpen}
        onOpenChange={setDeletionDialogOpen}
        tenantId={id}
        tenantName={tenant.name}
      />
    </div>
  );
}
